import React, { createContext, useEffect, useState } from "react";

import LoadingPage from "@pages/loading";
import ReConsentPage from "@pages/re-consent/re-consent-page";
import { useMe } from "@utils/api/queries";
import { useAppStore } from "@utils/app-store";
import i18n from "@utils/i18n";
import history from "@utils/router/history";
import { User } from "@utils/types";

export type UserContextType = {
  user?: User;
  getUser: () => Promise<void>;
  isLoading: boolean;
};

export const UserContext = createContext<UserContextType | null>(null);

const pathsWithoutMeQuery = ["/tac"];

type ContextProviderProps = {
  children: React.ReactNode;
};

const UserProvider = ({ children }: ContextProviderProps) => {
  const [pathname, setPathname] = useState(history.location.pathname);

  const shouldFetchMeQuery = !pathsWithoutMeQuery.includes(pathname);

  const { data, isLoading, refetch, error } = useMe(shouldFetchMeQuery);

  const user = data?.user;

  const { hasInitiallyFetched, setHasInitiallyFetched, setIsAuthenticated } =
    useAppStore();

  useEffect(() => {
    return history.listen(({ location }) => setPathname(location.pathname));
  }, []);

  useEffect(() => {
    if (shouldFetchMeQuery && !isLoading && !hasInitiallyFetched) {
      setHasInitiallyFetched();
    }
  }, [
    shouldFetchMeQuery,
    isLoading,
    hasInitiallyFetched,
    setHasInitiallyFetched,
  ]);

  // Sync isAuthenticated with user state
  useEffect(() => {
    setIsAuthenticated(Boolean(user));
  }, [user, setIsAuthenticated]);

  useEffect(() => {
    if (user?.language && user.language !== i18n.language) {
      i18n.changeLanguage(user.language);
    }
  }, [user]);

  const getUser = async () => {
    await refetch();
  };

  const requiresReConsent =
    shouldFetchMeQuery && error?.response?.status === 451;

  // Only for initial loading, we show a loading page.
  if (!hasInitiallyFetched && isLoading) {
    return <LoadingPage />;
  }

  return (
    <UserContext.Provider
      value={{
        user,
        getUser,
        isLoading,
      }}
    >
      {requiresReConsent ? <ReConsentPage /> : children}
    </UserContext.Provider>
  );
};

export default UserProvider;
